import React from 'react';
import { AlertTriangle, RefreshCw, Home, Mail } from 'lucide-react';
import errorReportingService from '../services/errorReportingService';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isReporting: false,
      reportSent: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
      isReporting: true
    });

    // Report the error
    this.reportError(error, errorInfo);
  }

  async reportError(error, errorInfo) {
    try {
      const success = await errorReportingService.reportFrontendError(error, errorInfo);
      this.setState({
        isReporting: false,
        reportSent: success
      });
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
      this.setState({
        isReporting: false,
        reportSent: false
      });
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleContactSupport = () => {
    window.location.href = 'mailto:paul@pestcontrol.co.uk?subject=PestCRM Error Report';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, isReporting, reportSent } = this.state;

      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            {/* Error Card */}
            <div className="bg-white rounded-lg shadow-xl border border-red-200 overflow-hidden">
              {/* Header */}
              <div className="bg-red-50 border-b border-red-200 p-6">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="text-red-500" size={24} />
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-red-800">
                      Oops! Something went wrong
                    </h1>
                    <p className="text-red-600 mt-1">
                      We've encountered an unexpected error in the application
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Error Status */}
                <div className="mb-6">
                  {isReporting ? (
                    <div className="flex items-center space-x-3 text-blue-600">
                      <RefreshCw size={20} className="animate-spin" />
                      <span>Reporting error to our team...</span>
                    </div>
                  ) : reportSent ? (
                    <div className="flex items-center space-x-3 text-green-600 bg-green-50 p-3 rounded-lg">
                      <Mail size={20} />
                      <div>
                        <p className="font-medium">Error reported successfully</p>
                        <p className="text-sm text-green-700">
                          Our development team has been notified and will investigate this issue.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3 text-amber-600 bg-amber-50 p-3 rounded-lg">
                      <AlertTriangle size={20} />
                      <div>
                        <p className="font-medium">Unable to report error automatically</p>
                        <p className="text-sm text-amber-700">
                          Please contact support if this issue persists.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* What happened */}
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-3">What happened?</h2>
                  <p className="text-gray-600 mb-4">
                    The application encountered an error while processing your request. This could be due to:
                  </p>
                  <ul className="text-gray-600 space-y-1 ml-4">
                    <li>• A temporary server issue</li>
                    <li>• Network connectivity problems</li>
                    <li>• An unexpected data format</li>
                    <li>• A bug in the application code</li>
                  </ul>
                </div>

                {/* Error Details (Development Only) */}
                {process.env.NODE_ENV === 'development' && error && (
                  <div className="mb-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-3">
                      Error Details (Development)
                    </h2>
                    <div className="bg-gray-50 rounded-lg p-4 overflow-auto">
                      <div className="text-sm space-y-2">
                        <div>
                          <span className="font-medium text-gray-700">Error:</span>{' '}
                          <span className="text-red-600">{error.name}: {error.message}</span>
                        </div>
                        {error.stack && (
                          <details className="mt-2">
                            <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                              Stack Trace
                            </summary>
                            <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">
                              {error.stack}
                            </pre>
                          </details>
                        )}
                        {errorInfo && errorInfo.componentStack && (
                          <details className="mt-2">
                            <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                              Component Stack
                            </summary>
                            <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">
                              {errorInfo.componentStack}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-3">
                  <h2 className="text-lg font-medium text-gray-900">What can you do?</h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      onClick={this.handleRefresh}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium"
                    >
                      <RefreshCw size={18} />
                      <span>Refresh Page</span>
                    </button>
                    
                    <button
                      onClick={this.handleGoHome}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 font-medium"
                    >
                      <Home size={18} />
                      <span>Go Home</span>
                    </button>
                    
                    <button
                      onClick={this.handleContactSupport}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 font-medium"
                    >
                      <Mail size={18} />
                      <span>Contact Support</span>
                    </button>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Need help?</span> If you continue to experience issues, 
                    please contact our support team at{' '}
                    <a 
                      href="mailto:paul@pestcontrol.co.uk" 
                      className="underline hover:no-underline"
                    >
                      paul@pestcontrol.co.uk
                    </a>
                    {reportSent && (
                      <span> and mention that an error report was automatically sent at {new Date().toLocaleString()}.</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;